import React from "react";
import { Select, Tooltip } from "antd";
import { FaCheckCircle, FaTimesCircle, FaMinusCircle } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import moment from "moment";

const { Option } = Select;

const getStatusIcon = (status) => {
  switch (status) {
    case "present":
      return <FaCheckCircle className="text-green-500 status-icon" />;
    case "absent":
      return <FaTimesCircle className="text-red-500 status-icon" />;
    case "leave":
      return <FaMinusCircle className="text-purple-500 status-icon" />;
    default:
      return null;
  }
};

const calendarStyles = `
  .calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0;
  }
  .calendar-tile {
    height: 80px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    border-radius: 0;
    transition: background 0.2s ease;
  }
  .calendar-tile:hover {
    background: #f5f5f5;
    cursor: pointer;
  }
  .calendar-tile.active {
    background: #b1d2d0;
    color: white;
    border-radius: 8px;
  }
  .weekday {
    text-align: center;
    font-weight: 600;
    color: #595959;
    padding: 10px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .status-container {
    margin-top: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
  }
  .status-icon {
    font-size: 16px;
  }
  .status-text {
    font-size: 12px;
    color: #000;
    text-transform: capitalize;
  }
`;

const CalendarHeader = ({
  attendanceData,
  onMonthChange,
  onYearChange,
  yearList,
  selectedMonth,
  selectedYear,
}) => {
  const { t } = useTranslation();

  // Build days in month with empty slots (force UTC to avoid +1 shift)
  const startOfMonth = moment.utc([selectedYear, selectedMonth]);
  const daysInMonth = startOfMonth.daysInMonth();
  const firstDay = startOfMonth.day(); // 0=Sun

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null); // empty slots
  for (let d = 1; d <= daysInMonth; d++) {
    calendarDays.push(
      moment.utc([selectedYear, selectedMonth, d]).format("YYYY-MM-DD")
    );
  }

  // Map attendance for fast lookup (normalize keys to YYYY-MM-DD UTC)
  const attendanceMap = {};
  attendanceData.forEach((item) => {
    const dateKey = moment.utc(item.date).format("YYYY-MM-DD");
    attendanceMap[dateKey] = {
      status: item.status === "not marked" ? " " : item.status,
      workingHours: item.workingHours || null,
    };
  });

  const months = moment.months();

  return (
    <div className="w-full">
      <style>{calendarStyles}</style>

      {/* Month & Year Dropdowns */}
      <div className="flex justify-end items-center mb-6 space-x-3">
        <Select
          style={{ width: 120, fontSize: "14px" }}
          value={selectedMonth}
          onChange={onMonthChange}
          dropdownStyle={{ minWidth: 120 }}
        >
          {months.map((monthName, index) => (
            <Option key={monthName} value={index}>
              {t(monthName)}
            </Option>
          ))}
        </Select>

        <Select
          style={{ width: 100, fontSize: "14px" }}
          value={selectedYear}
          onChange={onYearChange}
          dropdownStyle={{ minWidth: 100 }}
        >
          {yearList?.map((yr) => (
            <Option key={yr} value={yr}>
              {yr}
            </Option>
          ))}
        </Select>
      </div>

      {/* Weekday headings */}
      <div className="calendar-grid mb-2">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="weekday">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="calendar-grid">
        {calendarDays.map((date, idx) =>
          date ? (
            <Tooltip
              key={idx}
              title={() => {
                const record = attendanceMap[date];
                if (!record) return t("No Data");

                if (!record.startTime || !record.endTime) {
                  return t("No Data");
                }

                // calculate total hours
                const start = new Date(record.startTime);
                const end = new Date(record.endTime);
                const diffMs = end - start;

                const hours = Math.floor(diffMs / (1000 * 60 * 60));
                const minutes = Math.floor(
                  (diffMs % (1000 * 60 * 60)) / (1000 * 60)
                );

                return `${t("Working Hours")}: ${hours
                  .toString()
                  .padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${t(
                  "Hours"
                )}`;
              }}
            >
              <div className="calendar-tile">
                {/* FIX: use UTC for day number */}
                <span>{moment.utc(date).date()}</span>
                {attendanceMap[date]?.status && (
                  <div className="status-container">
                    {getStatusIcon(attendanceMap[date].status)}
                    <span className="status-text">
                      {attendanceMap[date].status}
                    </span>
                  </div>
                )}
              </div>
            </Tooltip>
          ) : (
            <div key={idx} className="calendar-tile"></div>
          )
        )}
      </div>
    </div>
  );
};

export default CalendarHeader;
